/**
 * Header class with mega menu, search, and mobile navigation handling
 */
class Header {
  constructor(container) {
      this.container = container;
      this.header = container.querySelector('#shopify-section-header header.header');
      this.mobileNav = container.querySelector('.mobile-nav');
      this.announcementBar = container.querySelector('[announcement-bar]');
      
      // Menu elements
      this.mobileMenuToggles = container.querySelectorAll('[mobile-nav-toggle]');
      this.mobileMenuPages = container.querySelectorAll('.mobile-menu-page');
      this.megaMenuContainer = container.querySelector('#mega-menu-container');
      
      // Search elements
      this.searchIcon = container.querySelector('.js-show-search');
      this.closeSearchIcon = container.querySelector('.search-close');
      this.searchPop = container.querySelector('header #search-pop');
      this.searchForm = container.querySelector('.inline-search-form');
      this.searchInput = container.querySelector('.inline-search-input');
      
      // State management
      this.currentMegaMenu = null;
      this.megaMenuTimeout = null;
      
      this.init();
  }

  init() {
      this.initMegaMenu();
      this.handleHamburgerMenu();
      this.handleMenuItem();
      this.handleSearchIcon();
      this.initAnimatedPlaceholder();
      this.handleSlidingMenu();
      this.setMobileNavPosition();
      
      // Event listeners
      this.bindEvents();
  }

  bindEvents() {
      window.addEventListener('resize', this.debounce(() => {
          this.setMobileNavPosition();
      }, 250));
  }

  /**
   * Initialize mega menu functionality
   */
  initMegaMenu() {
    if (!this.megaMenuContainer) return;

    // Fix: Query nav items correctly - they should have the mega-menu-placeholder
    const navItemsWithMegaMenu = this.container.querySelectorAll('.nav .li-link');
    
    navItemsWithMegaMenu.forEach(item => {
        const placeholder = item.querySelector('.mega-menu-placeholder');
        if (!placeholder) return;

        const menuHandle = placeholder.dataset.menu;
        const megaMenu = document.getElementById(`mega-menu-${menuHandle}`);
        
        if (!megaMenu) return;

        // Move mega menu to container if not already there
        if (!this.megaMenuContainer.contains(megaMenu)) {
            this.megaMenuContainer.appendChild(megaMenu);
        }

        // Bind hover events
        this.bindMegaMenuEvents(item, megaMenu);
    });
  }

  bindMegaMenuEvents(navItem, megaMenu) {
      const showMegaMenu = () => {
          this.clearMegaMenuTimeout();
          this.showMegaMenu(megaMenu);
          this.currentMegaMenu = megaMenu;
      };

      const hideMegaMenu = () => {
          this.megaMenuTimeout = setTimeout(() => {
              // Fix: Check if we're still hovering over the nav item OR the mega menu
              if (!navItem.matches(':hover') && !megaMenu.matches(':hover')) {
                  this.hideMegaMenu(megaMenu);
                  this.currentMegaMenu = null;
              }
          }, 150);
      };

      // Navigation item events
      navItem.addEventListener('mouseenter', showMegaMenu);
      navItem.addEventListener('mouseleave', hideMegaMenu);

      // Mega menu events
      megaMenu.addEventListener('mouseenter', () => {
          this.clearMegaMenuTimeout();
          this.showMegaMenu(megaMenu);
          this.currentMegaMenu = megaMenu;
      });
      
      megaMenu.addEventListener('mouseleave', hideMegaMenu);

      // Keyboard accessibility
      const navLink = navItem.querySelector('a');
      if (navLink) {
          navLink.addEventListener('focus', showMegaMenu);
          navLink.addEventListener('blur', () => {
              // Add delay for blur to allow clicking on mega menu
              setTimeout(() => {
                  if (!megaMenu.matches(':hover') && !navItem.matches(':focus-within')) {
                      this.hideMegaMenu(megaMenu);
                      this.currentMegaMenu = null;
                  }
              }, 100);
          });
      }
  }

  showMegaMenu(megaMenu) {
      // Hide all other mega menus first
      this.container.querySelectorAll('.mega-menu.show, .mega-menu.is-hiding').forEach(menu => {
          if (menu !== megaMenu) {
              this.hideMegaMenu(menu, true);
          }
      });

      if (megaMenu._hideAnimationTimeout) {
          clearTimeout(megaMenu._hideAnimationTimeout);
          delete megaMenu._hideAnimationTimeout;
      }

      megaMenu.classList.remove('is-hiding');
      megaMenu.classList.add('show');
      
      // Position the mega menu dynamically below the header
      this.positionMegaMenu(megaMenu);
      
  }

  hideMegaMenu(megaMenu, immediate = false) {
      if (!megaMenu) return;

      megaMenu.classList.remove('show');

      if (immediate) {
          if (megaMenu._hideAnimationTimeout) {
              clearTimeout(megaMenu._hideAnimationTimeout);
              delete megaMenu._hideAnimationTimeout;
          }

          megaMenu.classList.remove('is-hiding');
      } else {
          megaMenu.classList.add('is-hiding');

          if (megaMenu._hideAnimationTimeout) {
              clearTimeout(megaMenu._hideAnimationTimeout);
          }

          megaMenu._hideAnimationTimeout = setTimeout(() => {
              megaMenu.classList.remove('is-hiding');
              delete megaMenu._hideAnimationTimeout;
          }, 200);
      }
      
  }

  clearMegaMenuTimeout() {
      if (this.megaMenuTimeout) {
          clearTimeout(this.megaMenuTimeout);
          this.megaMenuTimeout = null;
      }
  }

  /**
   * Position mega menu below the current header height
   */
  positionMegaMenu(megaMenu) {
      if (!this.header || !megaMenu) return;
      
      const headerRect = this.header.getBoundingClientRect();
      const headerBottom = headerRect.bottom;
      
      megaMenu.style.top = `${headerBottom}px`;
  }

  setMobileNavPosition() {
      if (!this.mobileNav) return;

      const announcementBarHeight = this.announcementBar ? this.announcementBar.offsetHeight : 0;
      this.mobileNav.style.top = `${announcementBarHeight || 26}px`;
  }

  handleHamburgerMenu() {
      this.mobileMenuToggles.forEach(toggle => {
          toggle.addEventListener('click', (e) => {
              e.preventDefault();
              this.container.classList.toggle('open');
          });
      });
  }

  handleMenuItem() {
      // Use vanilla JS instead of jQuery for better performance
      const mobHeadings = document.querySelectorAll('.mob-heading');
      
      mobHeadings.forEach(heading => {
          heading.addEventListener('click', () => {
              const nextElement = heading.nextElementSibling;
              if (nextElement && nextElement.classList.contains('mobile-child')) {
                  const isExpanded = heading.classList.contains('expanded');
                  
                  if (isExpanded) {
                      nextElement.style.display = 'none';
                  } else {
                      nextElement.style.display = 'block';
                  }
                  
                  heading.classList.toggle('expanded');
              }
          });
      });

      // Hide all mobile children initially
      document.querySelectorAll('.mobile-child').forEach(child => {
          child.style.display = 'none';
      });
  }

  handleSearchIcon() {
      if (!this.searchIcon || !this.closeSearchIcon || !this.searchPop) return;

      // Search icon click
      this.searchIcon.addEventListener('click', this.openSearch.bind(this));
      
      // Close search icon click
      this.closeSearchIcon.addEventListener('click', this.closeSearch.bind(this));

      // Click outside to close
      document.addEventListener('click', (event) => {
          if (this.searchPop.classList.contains('show') && 
              !this.searchPop.contains(event.target) &&
              !this.searchIcon.contains(event.target)) {
              this.closeSearch();
          }
      });

      // Search form handling
      this.handleInlineSearch();
  }

  openSearch() {
      this.searchPop.classList.add('show');
      this.closeSearchIcon.style.display = 'block';
      document.body.classList.add('no-scroll');
      
      // Handle z-index for announcement bar
      const announcementSection = document.querySelector('#shopify-section-announcement-bar');
      if (announcementSection) {
          announcementSection.style.zIndex = '0';
      }

      // Hide needsclick element if it exists
      const needsClick = document.querySelector('.needsclick');
      if (needsClick) {
          needsClick.style.display = 'none';
      }

      // Focus search input
      if (this.searchInput) {
          setTimeout(() => this.searchInput.focus(), 100);
      }
  }

  closeSearch() {
      this.searchPop.classList.remove('show');
      this.closeSearchIcon.style.display = 'none';
      document.body.classList.remove('no-scroll');

      const announcementSection = document.querySelector('#shopify-section-announcement-bar');
      if (announcementSection) {
          announcementSection.style.zIndex = '';
      }

      const needsClick = document.querySelector('.needsclick');
      if (needsClick) {
          needsClick.style.display = 'block';
      }
  }

  handleInlineSearch() {
      if (!this.searchForm || !this.searchInput) return;

      // Form submit handler
      this.searchForm.addEventListener('submit', (e) => {
          const query = this.searchInput.value.trim();
          if (!query) {
              e.preventDefault();
              return;
          }
      });

      // Focus/blur handlers
      this.searchInput.addEventListener('focus', () => {
          this.searchForm.classList.add('focused');
      });

      this.searchInput.addEventListener('blur', () => {
          this.searchForm.classList.remove('focused');
      });

      // Search button handler
      const searchButton = document.querySelector('.inline-search-submit');
      if (searchButton) {
          searchButton.addEventListener('click', (e) => {
              e.preventDefault();
              const query = this.searchInput.value.trim();
              if (query) {
                  this.searchForm.submit();
              } else {
                  this.searchInput.focus();
              }
          });
      }

      // Enter key handler
      this.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
              e.preventDefault();
              const query = this.searchInput.value.trim();
              if (query) {
                  this.searchForm.submit();
              }
          }
      });

      // Search icon in search popup
      const searchPopIcon = document.querySelector('header #search-pop .search-input-container img');
      if (searchPopIcon) {
          searchPopIcon.addEventListener('click', () => {
              if (this.searchForm) {
                  this.searchForm.submit();
              }
          });
      }
  }

  handleSlidingMenu() {
    const menuPages = this.mobileMenuPages;
    if (!menuPages.length) return;

    // Parent menu item clicks - Fixed to handle all links with data-target-page
    this.container.querySelectorAll('a[data-target-page]').forEach(link => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            const targetPage = e.target.getAttribute('data-target-page') || e.currentTarget.getAttribute('data-target-page');
            if (!targetPage) return;

            this.switchMenuPage(menuPages, targetPage);
        });
    });

    // Back button clicks
    this.container.querySelectorAll('.back-btn').forEach(backButton => {
        backButton.addEventListener('click', (e) => {
            const targetPage = e.target.getAttribute('data-target-page') || e.currentTarget.getAttribute('data-target-page');
            if (targetPage) {
                this.switchMenuPage(menuPages, targetPage);
            }
        });
    });
  }

  switchMenuPage(menuPages, targetPage) {
      menuPages.forEach(page => {
          const pageId = page.getAttribute('data-page');
          
          if (pageId === targetPage) {
              page.classList.add('active');
              page.classList.remove('previous');
              
              const mobileChild = page.querySelector('.mobile-child');
              if (mobileChild) {
                  mobileChild.style.display = 'block';
              }
          } else if (page.classList.contains('active')) {
              page.classList.add('previous');
              page.classList.remove('active');
          } else {
              page.classList.remove('active', 'previous');
          }
      });
  }

  initAnimatedPlaceholder() {
      const placeholderContainers = document.querySelectorAll('.search-placeholder-animated');
      const searchInputs = document.querySelectorAll('.inline-search-input');
      
      if (!placeholderContainers.length || !searchInputs.length) return;

      const phrases = this.getLocalizedPhrases();

      placeholderContainers.forEach((placeholderContainer, index) => {
          const correspondingInput = searchInputs[index];
          if (!correspondingInput) return;

          this.initTypewriter(placeholderContainer, correspondingInput, phrases);
      });
  }

  getLocalizedPhrases() {
      const englishPhrases = ['Try searching for...', 'Midi dress', 'Shirt'];
      const croatianPhrases = ['Pokušajte pretraživati...', 'Midi haljina', 'Košulja'];
      const serbianPhrases = ['Pokušajte pretraživati...', 'Midi haljina', 'Košulja'];
      
      const locale = (typeof Shopify !== 'undefined' && Shopify.locale) ? Shopify.locale : 'en';
      switch (locale) {
        case 'hr':
          return croatianPhrases;
        case 'sr':
          return serbianPhrases;
        default:
          return englishPhrases;
      }
  }

  initTypewriter(placeholderContainer, input, phrases) {
      let currentPhraseIndex = 0;
      let currentCharIndex = 0;
      let isDeleting = false;
      let isPaused = false;

      const typeWriter = () => {
          const currentPhrase = phrases[currentPhraseIndex];
          const typingElement = placeholderContainer.querySelector('.typing-text');
          
          if (!typingElement) return;

          if (!isDeleting && currentCharIndex < currentPhrase.length) {
              typingElement.textContent = currentPhrase.substring(0, currentCharIndex + 1);
              currentCharIndex++;
              setTimeout(typeWriter, 100);
          } else if (!isDeleting && currentCharIndex === currentPhrase.length) {
              if (!isPaused) {
                  isPaused = true;
                  setTimeout(() => {
                      isPaused = false;
                      isDeleting = true;
                      typeWriter();
                  }, 2000);
              }
          } else if (isDeleting && currentCharIndex > 0) {
              typingElement.textContent = currentPhrase.substring(0, currentCharIndex - 1);
              currentCharIndex--;
              setTimeout(typeWriter, 50);
          } else if (isDeleting && currentCharIndex === 0) {
              isDeleting = false;
              currentPhraseIndex = (currentPhraseIndex + 1) % phrases.length;
              setTimeout(typeWriter, 500);
          }
      };

      // Start typewriter
      typeWriter();

      // Input event listeners
      input.addEventListener('focus', () => {
          placeholderContainer.style.opacity = '0';
      });

      input.addEventListener('blur', () => {
          if (!input.value) {
              placeholderContainer.style.opacity = '1';
          }
      });

      input.addEventListener('input', () => {
          placeholderContainer.style.opacity = input.value ? '0' : '1';
      });
  }

  // Utility function for debouncing
  debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
          const later = () => {
              clearTimeout(timeout);
              func(...args);
          };
          clearTimeout(timeout);
          timeout = setTimeout(later, wait);
      };
  }
}

/**
* Optimized Wishlist Counter class
*/
class WishlistCounter {
  constructor(wishlistKey, baseCounterElementId, options = {}) {
      this.wishlistKey = wishlistKey;
      this.mobileCounterElement = document.getElementById(`${baseCounterElementId}-mobile`);
      this.desktopCounterElement = document.getElementById(`${baseCounterElementId}-desktop`);
      
      this.apiUrl = options.apiUrl || 'https://wishlist.solucommerce.com/api/v1/storefront/wishlists';
      this.useApiInterception = options.useApiInterception !== false;
      this.apiData = null;
      this.lastApiUpdate = null;
      
      this.isLoading = true;
      this.apiResponseReceived = false;
      this.loadingTimeout = null;
      this.apiWaitTime = options.apiWaitTime || 2000;
      
      this.init();
  }

  init() {
      if (!this.mobileCounterElement && !this.desktopCounterElement) {
          return;
      }

      if (this.useApiInterception) {
          this.interceptRequests();
      }

      this.startLoadingState();
      this.bindEvents();
      this.overrideLocalStorage();
  }

  bindEvents() {
      window.addEventListener('storage', (event) => {
          if (event.key === this.wishlistKey && !this.isLoading) {
              this.updateCounter();
          }
      });

      document.addEventListener('wishlist-updated', () => {
          if (!this.isLoading) {
              this.updateCounter();
          }
      });
  }

  startLoadingState() {
      this.isLoading = true;
      this.apiResponseReceived = false;
      this.hideCounters();
      
      this.loadingTimeout = setTimeout(() => {
          if (!this.apiResponseReceived) {
              this.isLoading = false;
              this.updateCounter();
          }
      }, this.apiWaitTime);
  }

  hideCounters() {
      [this.mobileCounterElement, this.desktopCounterElement].forEach(element => {
          if (element) element.classList.add('hidden');
      });
  }

  interceptRequests() {
      this.interceptFetchRequests();
      this.interceptXHRRequests();
  }

  interceptFetchRequests() {
      const originalFetch = window.fetch;
      const self = this;
      
      window.fetch = function(...args) {
          const [resource] = args;
          const url = typeof resource === 'string' ? resource : resource.url;
          
          if (url.includes('/api/v1/storefront/wishlists')) {
              return originalFetch.apply(this, args).then(response => {
                  const clonedResponse = response.clone();
                  
                  clonedResponse.json().then(data => {
                      if (data?.success) {
                          self.handleApiResponse(data);
                      }
                  }).catch(() => {
                      self.handleApiError();
                  });
                  
                  return response;
              }).catch(error => {
                  self.handleApiError();
                  throw error;
              });
          }
          
          return originalFetch.apply(this, args);
      };
  }

  interceptXHRRequests() {
      const originalOpen = XMLHttpRequest.prototype.open;
      const originalSend = XMLHttpRequest.prototype.send;
      const self = this;
      
      XMLHttpRequest.prototype.open = function(method, url, ...args) {
          this._url = url;
          return originalOpen.apply(this, [method, url, ...args]);
      };
      
      XMLHttpRequest.prototype.send = function(...args) {
          if (this._url?.includes('/api/v1/storefront/wishlists')) {
              this.addEventListener('load', function() {
                  try {
                      if (this.status >= 200 && this.status < 300) {
                          const data = JSON.parse(this.responseText);
                          if (data?.success) {
                              self.handleApiResponse(data);
                          }
                      } else {
                          self.handleApiError();
                      }
                  } catch (error) {
                      // Silently handle non-JSON responses
                  }
              });
              
              this.addEventListener('error', () => {
                  self.handleApiError();
              });
          }
          
          return originalSend.apply(this, args);
      };
  }

  handleApiResponse(data) {
      this.apiData = data;
      this.lastApiUpdate = Date.now();
      this.apiResponseReceived = true;
      
      if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
      }
      
      this.isLoading = false;
      this.updateCounter();
  }

  handleApiError() {
      console.warn('Wishlist API request failed, will fallback to localStorage');
  }

  updateCounter() {
      if (this.isLoading) return;

      let wishlistCount = 0;
      const fiveMinutes = 5 * 60 * 1000;
      const hasRecentApiData = this.apiData && 
                              this.lastApiUpdate && 
                              (Date.now() - this.lastApiUpdate) < fiveMinutes;

      if (this.useApiInterception && hasRecentApiData) {
          wishlistCount = this.calculateApiWishlistCount(this.apiData.data);
      } else {
          const wishlistData = this.getWishlistData();
          wishlistCount = wishlistData.length;
      }

      this.updateCounterElements(wishlistCount);
  }

  calculateApiWishlistCount(apiData) {
      if (!Array.isArray(apiData)) return 0;
      
      return apiData.reduce((total, wishlist) => {
          return total + (wishlist.products ? wishlist.products.length : 0);
      }, 0);
  }

  updateCounterElements(count) {
      [this.mobileCounterElement, this.desktopCounterElement].forEach(element => {
          if (!element) return;

          if (count > 0) {
              element.textContent = count;
              element.classList.remove('hidden');
          } else {
              element.classList.add('hidden');
          }
      });
  }

  getWishlistData() {
      try {
          return JSON.parse(localStorage.getItem(this.wishlistKey)) || [];
      } catch (error) {
          return [];
      }
  }

  overrideLocalStorage() {
      const originalSetItem = localStorage.setItem;
      const originalRemoveItem = localStorage.removeItem;

      localStorage.setItem = (key, value) => {
          originalSetItem.apply(localStorage, [key, value]);
          if (key === this.wishlistKey) {
              document.dispatchEvent(new Event('wishlist-updated'));
          }
      };

      localStorage.removeItem = (key) => {
          originalRemoveItem.apply(localStorage, [key]);
          if (key === this.wishlistKey) {
              document.dispatchEvent(new Event('wishlist-updated'));
          }
      };
  }

  // Public methods
  setApiData(data) {
      this.handleApiResponse(data);
  }

  setUseApiInterception(useInterception) {
      this.useApiInterception = useInterception;
      if (!this.isLoading) {
          this.updateCounter();
      }
  }

  forceUpdate() {
      this.startLoadingState();
  }

  restartLoading() {
      this.startLoadingState();
  }
}

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', () => {
  // Initialize Header
  new Header(document.body);
  
  // Initialize Wishlist Counter
  const wishlistCounter = new WishlistCounter('st-wishlist-local', 'wishlist-counter', {
      useApiInterception: true,
      apiWaitTime: 2000
  });
  
  // Make wishlist counter globally accessible
  window.wishlistCounter = wishlistCounter;
});
